import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Rocket, Loader2, AlertTriangle, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const STATUS_LABELS = {
  declaree: 'Déclarée', mandat_signe: 'Mandat signé', active: 'Active',
  impayee: 'Impayée', resiliee: 'Résiliée',
};

export default function SubscriptionLaunchPanel() {
  const [overview, setOverview] = useState(null);
  const [date, setDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [togglingVis, setTogglingVis] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/subscriptions/overview`, { withCredentials: true });
      setOverview(res.data);
    } catch (e) { /* silencieux */ }
  }, []);

  const toggleVisibility = async () => {
    setTogglingVis(true);
    try {
      const next = !(overview?.subscriptions_public);
      await axios.post(`${API}/admin/subscriptions/visibility`, { visible: next }, { withCredentials: true });
      toast.success(next ? 'Parcours mandat SEPA rendu visible par les clientes.' : 'Parcours mandat SEPA masqué côté clientes.');
      loadOverview();
    } catch (e) {
      toast.error('Impossible de modifier la visibilité.');
    } finally { setTogglingVis(false); }
  };

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const run = async (dryRun) => {
    if (!date) { toast.error('Choisissez la date du 1er prélèvement.'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/subscriptions/launch`,
        { first_charge_date: date, dry_run: dryRun }, { withCredentials: true });
      setPreview(res.data);
      if (!dryRun) {
        toast.success(`${res.data.launched.length} abonnement(s) créé(s) · ${res.data.skipped.length} ignoré(s) · ${res.data.errors.length} erreur(s).`);
        setConfirmOpen(false);
        loadOverview();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec du lancement.');
    } finally { setLoading(false); }
  };

  return (
    <Card className="border border-[#E9DDCC] rounded-2xl bg-white" data-testid="subscription-launch-panel">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Rocket className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Abonnements SEPA</span>
        </div>
        <h3 className="font-heading text-lg text-[#493329]">Lancer les abonnements</h3>

        {/* Visibilité du parcours mandat côté clientes */}
        <div className="flex items-center justify-between gap-3 bg-[#F5EFE5] border border-[#E9DDCC] rounded-xl px-4 py-3" data-testid="subs-visibility-row">
          <div className="text-sm text-[#493329]">
            <strong>Parcours mandat SEPA côté clientes :</strong>{' '}
            <span className={overview?.subscriptions_public ? 'text-emerald-700' : 'text-[#C76542]'}>
              {overview?.subscriptions_public ? 'Visible (publié)' : 'Masqué'}
            </span>
            <p className="text-[11px] text-[#AE9885] mt-0.5">Quand il est masqué, les clientes ne voient pas l'étape « Signer mon mandat SEPA » (recommandé avant l'ouverture / le passage en LIVE).</p>
          </div>
          <Button variant="outline" onClick={toggleVisibility} disabled={togglingVis}
            className="rounded-full border-[#C99A55]/50 text-[#9A744C] hover:bg-[#C99A55] hover:text-white h-9 shrink-0"
            data-testid="subs-visibility-toggle">
            {togglingVis ? <Loader2 className="w-4 h-4 animate-spin" /> : (overview?.subscriptions_public ? 'Masquer' : 'Publier')}
          </Button>
        </div>

        {/* Statuts */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center" data-testid="subs-overview">
            {Object.entries(STATUS_LABELS).map(([k, label]) => (
              <div key={k} className="bg-[#F5EFE5] rounded-lg p-2.5">
                <div className="text-[10px] uppercase text-[#AE9885]">{label}</div>
                <div className="font-heading text-xl text-[#493329]" data-testid={`subs-count-${k}`}>{overview.counts[k]}</div>
              </div>
            ))}
          </div>
        )}
        {overview && (overview.confirmed_without_mandate > 0 || overview.payment_alerts > 0) && (
          <div className="flex flex-wrap gap-2 text-xs">
            {overview.confirmed_without_mandate > 0 && (
              <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2.5 py-1" data-testid="no-mandate-badge">
                <CreditCard className="w-3.5 h-3.5" /> {overview.confirmed_without_mandate} confirmée(s) sans mandat
              </span>
            )}
            {overview.payment_alerts > 0 && (
              <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 rounded-full px-2.5 py-1" data-testid="payment-alerts-badge">
                <AlertTriangle className="w-3.5 h-3.5" /> {overview.payment_alerts} impayé(s)
              </span>
            )}
          </div>
        )}

        {/* Date + actions */}
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="text-xs text-[#7A6455] font-medium">Date du 1er prélèvement</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="mt-1 h-9 rounded-lg border border-[#E9DDCC] bg-white px-2.5 text-sm text-[#493329] focus:outline-none focus:ring-2 focus:ring-[#C99A55]"
              data-testid="launch-date-input" />
          </label>
          <Button variant="outline" onClick={() => run(true)} disabled={loading}
            className="rounded-full border-[#C99A55]/50 text-[#9A744C] hover:bg-[#C99A55] hover:text-white h-9"
            data-testid="launch-preview-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Prévisualiser'}
          </Button>
          <Button onClick={() => setConfirmOpen(true)} disabled={loading || !preview || preview.launched.length === 0}
            className="rounded-full bg-[#C76542] hover:bg-[#b2573a] text-white h-9 disabled:opacity-40"
            data-testid="launch-run-btn">
            <Rocket className="w-4 h-4 mr-1" /> Lancer les abonnements
          </Button>
        </div>

        <p className="text-[11px] text-[#AE9885] italic">
          Seules les clientes avec un mandat SEPA signé et une formule choisie sont concernées. Aucun prélèvement avant la date choisie.
          Les fondatrices bénéficient de −{overview ? '' : ''}20 % pendant 12 mois puis −5 % à vie (appliqué automatiquement par Stripe).
        </p>

        {/* Résultat */}
        {preview && (
          <div className="rounded-xl border border-[#E9DDCC] bg-[#FAF6EF] p-4 text-sm space-y-2" data-testid="launch-result">
            <div className="font-medium text-[#493329]">
              {preview.dry_run ? 'Prévisualisation' : 'Résultat'} — {preview.eligible} éligible(s), {preview.launched.length} à lancer, {preview.skipped.length} ignorée(s), {preview.errors.length} erreur(s)
            </div>
            {preview.launched.length > 0 && (
              <ul className="text-xs text-[#7A6455] space-y-0.5 max-h-40 overflow-y-auto">
                {preview.launched.map((l) => (
                  <li key={l.email}>• {l.email} — {l.plan} {l.founder ? '(fondatrice −20 %)' : ''} {l.stripe ? `✓ ${l.stripe}` : ''}</li>
                ))}
              </ul>
            )}
            {preview.errors.length > 0 && (
              <ul className="text-xs text-red-600 space-y-0.5">
                {preview.errors.map((e) => <li key={e.email}>⚠ {e.email} : {e.error}</li>)}
              </ul>
            )}
          </div>
        )}
      </CardContent>

      {/* Confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="launch-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#493329]">Confirmer le lancement</DialogTitle>
            <DialogDescription>
              Vous allez créer {preview?.launched.length} abonnement(s) Stripe avec un 1er prélèvement le <strong>{date}</strong>. Cette action engage les prélèvements à cette date.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="launch-cancel-btn">Annuler</Button>
            <Button onClick={() => run(false)} disabled={loading}
              className="bg-[#C76542] hover:bg-[#b2573a] text-white" data-testid="launch-confirm-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Rocket className="w-4 h-4 mr-1" />}
              Confirmer le lancement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
